import { useState, useEffect } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { useChatPanel } from "../../../context/ChatPanelContext";
import SafeTrans from "../../../i18n/SafeTrans";
import EvalSidebar from "./EvalSidebar";
import EvalHeader from "./EvalHeader";
import { EVAL_CHAT_ENABLED, EVAL_PAGE_LDAP_GROUP } from "../../../config";
import { EVAL_CHAT_AGENT_ID } from "../../../context/AgentContext";
import { isCompact, useBreakpoint } from "../../../hooks/useBreakpoint";
import ChatPanel from "../../../components/ChatPanel/ChatPanel";
import "./EvalLayout.css";
import { evalAgentLabel } from "../../agents";

const THEME_KEY = "eval-theme";
const SIDEBAR_KEY = "eval-sidebar-collapsed";
const AGENT_KEY = "eval-agent";
const CHAT_KEY = "eval-chat-collapsed";
const DEFAULT_AGENT = "rocky";


// Known path segments have a label; any other segment is title-cased from the URL.
const CRUMB_KEYS = {
  usage: "crumbs.usage",
  insights: "crumbs.insights",
  tasks: "crumbs.tasks",
  reports: "crumbs.reports",
  datasets: "crumbs.datasets",
  candidates: "crumbs.candidates",
  evaluators: "crumbs.evaluators",
  metrics: "crumbs.metrics",
  settings: "crumbs.settings",
} as const;

function getBreadcrumb(pathname: string, agentLabel: string, t: TFunction<"eval">): { label: string; to?: string }[] {
  const crumbs: { label: string; to?: string }[] = [
    { label: t("crumbs.eval"), to: "/eval" },
    { label: agentLabel, to: "/eval" },
  ];
  const parts = pathname.replace("/eval", "").split("/").filter(Boolean);
  if (parts.length === 0) {
    crumbs.push({ label: t("crumbs.homepage") });
  } else {
    parts.forEach((p, i) => {
      const key = CRUMB_KEYS[p as keyof typeof CRUMB_KEYS];
      const label = key ? t(key) : p.charAt(0).toUpperCase() + p.slice(1).replace(/-/g, " ");
      if (i < parts.length - 1) {
        crumbs.push({ label, to: "/eval/" + parts.slice(0, i + 1).join("/") });
      } else {
        crumbs.push({ label });
      }
    });
  }
  return crumbs;
}

export default function EvalLayout() {
  const { t } = useTranslation("eval");
  const { uid, ldap_groups } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [theme, setTheme] = useState<"dark" | "light">(() => {
    const saved = localStorage.getItem(THEME_KEY);
    return saved === "dark" ? "dark" : "light";
  });

  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    const saved = localStorage.getItem(SIDEBAR_KEY);
    return saved === "false" ? false : true;
  });

  const [agent, setAgent] = useState<string>(() => {
    const fromUrl = searchParams.get("agent");
    if (fromUrl) return fromUrl;
    const saved = localStorage.getItem(AGENT_KEY);
    return saved || DEFAULT_AGENT;
  });

  // The eval chat keeps its OWN collapsed state (not ChatPanelContext's, which
  // belongs to the main app's ChatShell). Starts collapsed until first opened.
  const [chatCollapsed, setChatCollapsed] = useState<boolean>(() => {
    const saved = localStorage.getItem(CHAT_KEY);
    return saved === "false" ? false : true;
  });

  useEffect(() => {
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem(CHAT_KEY, String(chatCollapsed));
  }, [chatCollapsed]);

  // Fullscreen is shared app state (ChatPanelContext). The eval chat has its own
  // toggle for it, but a fullscreen left on in the main app is not the user asking
  // for it here: drop it on entering /eval, and drop the eval one on leaving, so
  // neither surface opens the other's chat fullscreen.
  const { chatFullscreen, exitFullscreen, pendingPrompt } = useChatPanel();
  useEffect(() => {
    if (chatFullscreen) exitFullscreen();
    return () => exitFullscreen();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // The pages' "ask the eval agent" buttons (AskEvalAgent) send through
  // ChatPanelContext.sendPrompt, which only expands the main app's panel. The eval
  // chat sends the prompt even while collapsed, so open it here to show the answer.
  useEffect(() => {
    if (pendingPrompt) setChatCollapsed(false);
  }, [pendingPrompt]);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_KEY, String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  useEffect(() => {
    localStorage.setItem(AGENT_KEY, agent);
  }, [agent]);

  // Pick up agent from URL param when navigating from landing page
  useEffect(() => {
    const fromUrl = searchParams.get("agent");
    if (fromUrl && fromUrl !== agent) {
      setAgent(fromUrl);
    }
  }, [searchParams]);

  // Below desktop the expanded sidebar overlays the content (EvalLayout.css) — a
  // drawer, not a column. Its open state is kept apart from `sidebarCollapsed` on
  // purpose: that one is the persisted DESKTOP preference, and opening the drawer
  // once on a phone must not leave the desktop sidebar expanded next time. The
  // drawer starts closed and closes on every navigation.
  const compact = isCompact(useBreakpoint());
  const [drawerOpen, setDrawerOpen] = useState(false);
  useEffect(() => setDrawerOpen(false), [location.pathname]);
  const effectiveCollapsed = compact ? !drawerOpen : sidebarCollapsed;

  // Fullscreen only means something while the eval chat is shown and expanded. It
  // hides the page column (kept mounted, so filters and an open run survive) and
  // leaves the eval sidebar for navigation.
  const chatShown = EVAL_CHAT_ENABLED && !compact;
  const chatFull = chatShown && !chatCollapsed && chatFullscreen;
  const toggleChat = () => {
    // Collapsing leaves fullscreen, or the next expand would cover the page again.
    if (!chatCollapsed && chatFullscreen) exitFullscreen();
    setChatCollapsed((prev) => !prev);
  };

  const toggleTheme = () => setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  const toggleSidebar = compact
    ? () => setDrawerOpen((prev) => !prev)
    : () => setSidebarCollapsed((prev) => !prev);

  if (EVAL_PAGE_LDAP_GROUP && !ldap_groups.includes(EVAL_PAGE_LDAP_GROUP)) {
    return (
      <div className="eval-layout__denied" data-theme="dark">
        <div className="eval-layout__denied-card">
          <div className="eval-layout__denied-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h2 className="eval-layout__denied-title">{t("denied.title")}</h2>
          <p className="eval-layout__denied-desc">
            <SafeTrans t={t} i18nKey="denied.account" values={{ uid }} components={{ strong: <strong /> }} />
          </p>
          <p className="eval-layout__denied-detail">
            <SafeTrans t={t} i18nKey="denied.group" values={{ group: EVAL_PAGE_LDAP_GROUP }} components={{ code: <code /> }} />
          </p>
          <p className="eval-layout__denied-hint">
            {t("denied.hint")}
          </p>
          <a className="eval-layout__denied-back" href="/">
            {t("denied.back")}
          </a>
        </div>
      </div>
    );
  }

  const breadcrumbs = getBreadcrumb(location.pathname, evalAgentLabel(agent), t);

  return (
    <div className="eval-layout" data-theme={theme} data-sidebar={effectiveCollapsed ? "collapsed" : "expanded"}>
      <EvalHeader
        theme={theme}
        onToggleTheme={toggleTheme}
        uid={uid}
        sidebarCollapsed={effectiveCollapsed}
        onToggleSidebar={toggleSidebar}
      />
      <div className="eval-body">
        <EvalSidebar
          collapsed={effectiveCollapsed}
          onToggle={toggleSidebar}
          agent={agent}
          onAgentChange={setAgent}
        />
        {compact && drawerOpen && (
          <div className="eval-layout__backdrop" onClick={() => setDrawerOpen(false)} aria-hidden="true" />
        )}
        <div className="eval-content" style={chatFull ? { display: "none" } : undefined}>
          <nav className="eval-content__breadcrumb">
            {breadcrumbs.map((crumb, i) => (
              <span key={i}>
                {i > 0 && <span className="eval-content__breadcrumb-sep">{">"}</span>}
                {crumb.to ? (
                  <Link className="eval-content__breadcrumb-link" to={crumb.to}>
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="eval-content__breadcrumb-current">{crumb.label}</span>
                )}
              </span>
            ))}
          </nav>
          <Outlet context={{ agent }} />
        </div>
        {/* Chat with the Evaluation Agent — pinned to EVAL_CHAT_AGENT_ID, which is
            not a "Switch Agent" option. `agent` above is the EVALUATED-agent
            filter, unrelated to this chat. Desktop only for now. The /eval route
            already wraps this layout in CloudscapeI18nBoundary. */}
        {chatShown && (
          <ChatPanel
            isCollapsed={chatCollapsed}
            onToggle={toggleChat}
            agentName={EVAL_CHAT_AGENT_ID}
          />
        )}
      </div>
    </div>
  );
}
